/**
 * MarkdownEditor - WYSIWYG editor that works with Markdown content
 * Uses TipTap for editing and converts to/from Markdown
 */
import React, { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import TurndownService from 'turndown';
import { marked } from 'marked';
import { 
  Bold, Italic, List, ListOrdered, 
  Link as LinkIcon, Heading2, Heading3,
  Undo, Redo, Quote, Code
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Initialize Turndown for HTML to Markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced'
});

// Configure marked for Markdown to HTML
marked.setOptions({
  breaks: true,
  gfm: true
});

// Convert Markdown to HTML
const markdownToHtml = (markdown) => {
  if (!markdown) return '';
  try {
    return marked.parse(markdown);
  } catch (e) {
    console.error('Error parsing markdown:', e);
    return markdown;
  }
};

// Convert HTML to Markdown
const htmlToMarkdown = (html) => {
  if (!html) return '';
  try {
    return turndownService.turndown(html);
  } catch (e) {
    console.error('Error converting to markdown:', e);
    return html;
  }
};

// Toolbar button component
const ToolbarButton = ({ onClick, isActive, children, title, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={cn(
      "p-1.5 rounded transition-colors",
      "hover:bg-gray-200 dark:hover:bg-gray-700",
      isActive && "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400",
      disabled && "opacity-50 cursor-not-allowed"
    )}
  >
    {children}
  </button>
);

// Editor toolbar
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;
  
  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = prompt('Enter URL:', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url }).run();
  };
  
  return (
    <div className="flex items-center gap-0.5 p-2 border-b border-gray-200 dark:border-gray-700 flex-wrap bg-gray-50 dark:bg-gray-800/50">
      {/* Undo/Redo */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (Ctrl+Z)"
      >
        <Undo className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (Ctrl+Y)"
      >
        <Redo className="w-4 h-4" />
      </ToolbarButton>
      
      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
      
      {/* Headings */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <Heading2 className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        <Heading3 className="w-4 h-4" />
      </ToolbarButton>
      
      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
      
      {/* Text formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
      >
        <Bold className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
      >
        <Italic className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        isActive={editor.isActive('code')}
        title="Code"
      >
        <Code className="w-4 h-4" />
      </ToolbarButton>
      
      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
      
      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </ToolbarButton>
      
      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
      
      {/* Quote */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Quote"
      >
        <Quote className="w-4 h-4" />
      </ToolbarButton>
      
      {/* Link */}
      <ToolbarButton
        onClick={addLink}
        isActive={editor.isActive('link')}
        title="Add Link"
      >
        <LinkIcon className="w-4 h-4" />
      </ToolbarButton>
    </div>
  );
};

/**
 * MarkdownEditor component
 * @param {string} content - Markdown content
 * @param {function} onChange - Callback when markdown changes
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 * @param {number} minHeight - Minimum height in pixels
 * @param {boolean} disabled - Disable editing
 */
const MarkdownEditor = ({ 
  content, 
  onChange, 
  placeholder = 'Start writing...', 
  className = '',
  minHeight = 150,
  disabled = false
}) => {
  // Track if user has focused the editor (real interaction)
  const hasUserInteracted = React.useRef(false);
  
  // Convert initial markdown to HTML
  const initialHtml = useMemo(() => markdownToHtml(content), []);
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
        link: false, // Disable default Link to use our custom one
      }),
      Link.configure({ 
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300',
        },
      }),
      Placeholder.configure({ 
        placeholder,
      }),
      Typography,
    ],
    content: initialHtml,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none dark:prose-invert focus:outline-none p-3',
          'prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-gray-100',
          'prose-headings:mt-4 prose-headings:mb-2',
          'prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:my-2',
          'prose-li:text-gray-700 dark:prose-li:text-gray-300',
          'prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-strong:font-semibold',
          'prose-em:text-gray-700 dark:prose-em:text-gray-300',
          'prose-code:text-pink-600 dark:prose-code:text-pink-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1 prose-code:rounded',
          'prose-blockquote:border-l-blue-500 prose-blockquote:bg-blue-50 dark:prose-blockquote:bg-blue-900/20 prose-blockquote:px-4 prose-blockquote:py-1 prose-blockquote:not-italic',
          'prose-ul:my-2 prose-ol:my-2',
          disabled && 'cursor-not-allowed opacity-70'
        ),
        style: `min-height: ${minHeight}px`,
      },
      // Track when user focuses the editor
      handleDOMEvents: {
        focus: () => {
          hasUserInteracted.current = true;
          return false;
        },
      },
    },
    onUpdate: ({ editor }) => {
      // Only trigger onChange if user has actually interacted with the editor
      if (!hasUserInteracted.current) {
        return;
      }
      
      if (onChange && !disabled) {
        const html = editor.getHTML();
        const markdown = htmlToMarkdown(html);
        onChange(markdown);
      }
    },
  });

  // Update content when prop changes externally (but don't trigger onChange)
  useEffect(() => {
    if (editor && content !== undefined) {
      // Temporarily disable user interaction flag to prevent onChange
      const wasInteracted = hasUserInteracted.current;
      hasUserInteracted.current = false;
      
      const currentMarkdown = htmlToMarkdown(editor.getHTML());
      if (content !== currentMarkdown) {
        const newHtml = markdownToHtml(content);
        editor.commands.setContent(newHtml);
      }
      
      // Restore the flag after a tick
      setTimeout(() => {
        hasUserInteracted.current = wasInteracted;
      }, 0);
    }
  }, [content, editor]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  return (
    <div className={cn(
      "border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 flex flex-col",
      disabled && "opacity-80",
      className
    )}>
      {!disabled && <EditorToolbar editor={editor} />}
      <div className="flex-1 overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export { MarkdownEditor };
export default MarkdownEditor;
